import { Alert, Button, Input, Modal, Typography } from 'antd';
import { useState } from 'react';
import { FileTextOutlined } from '@ant-design/icons';
import Tooltip from 'antd/es/tooltip';
import TextArea from 'antd/lib/input/TextArea';
import rfsTheme from '../../../theme';
import { format } from 'date-fns';
import { tableDateTimeFormat } from '../../../lib/utils/date-time';

// The key that a user must enter to archive a team
const archiveKey = 'archive team';

/**
 * When a user attempts to archive a team, they must provide both a reason
 * and type the archiveKey.
 */
const formIsInvalid = (archiveTeamText: string, reason: string): boolean => {
  // TODO: Maybe? Ask RFS about reason validation rules
  // Maybe this should be a select box with a list of reasons
  // and an input box for "Other" reasons?
  return archiveTeamText !== archiveKey || reason === '';
};

export const ArchiveTeamButton: React.FC<{
  readonly onArchive: (reason: string) => void;
}> = ({ onArchive }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [archiveTeamText, setArchiveTeamText] = useState('');
  const [reason, setReason] = useState<string>('');

  return (
    <>
      <Tooltip title="Archive Team">
        <Button
          className="archive-team-btn"
          onClick={() => setModalOpen(true)}
          type="primary"
          icon={<FileTextOutlined />}
        >
          Archive Team
        </Button>
      </Tooltip>

      <Modal
        open={modalOpen}
        aria-label={'delete modal'}
        okText={'Archive Team'}
        okType={'danger'}
        cancelText={'Cancel'}
        onOk={() => onArchive(reason)}
        onCancel={() => {
          setModalOpen(false);
          setReason('');
          setArchiveTeamText('');
        }}
        destroyOnClose={true}
        keyboard={true}
        okButtonProps={{
          type: 'primary',
          danger: true,
          disabled: formIsInvalid(archiveTeamText, reason),
          className: 'confirm-archive-team-btn',
        }}
        width={'95vw'}
        style={{ maxWidth: '900px' }}
      >
        <Typography style={{ fontSize: '1.8rem', textAlign: 'center' }}>
          Confirm Archive Team
        </Typography>

        <Alert
          showIcon
          message="Once a team is archived:"
          description={
            <div style={{ paddingLeft: '15px' }}>
              <ol
                style={{
                  listStylePosition: 'inside',
                  paddingLeft: 0,
                }}
              >
                <li>
                  The IMT will be closed and users will be unable to edit or
                  change shifts until or unless the team is reactivated.
                </li>
                <li>All future shift assignments will be removed.</li>
                <li>
                  In-progress shifts will be ended as of{' '}
                  {format(new Date(), tableDateTimeFormat)}.
                </li>
                <li>
                  All users who have been assigned roles in this team will be
                  unassigned from those roles.
                </li>
                <li>Previously completed shifts will not be deleted.</li>
              </ol>
            </div>
          }
          type="warning"
        />

        <br />

        <div>
          <p>Why are you archiving this team?</p>
          <TextArea
            id="archive-reason-input"
            rows={4}
            placeholder="The incident..."
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            maxLength={1000}
            showCount
          />
        </div>

        <p
          style={{
            fontStyle: 'italic',
            color: rfsTheme.rfsRed,
            marginTop: 5,
            marginBottom: 5,
          }}
        >
          {`Type "${archiveKey}" below to confirm.`}
        </p>

        <Input
          className="archive-team-input"
          placeholder={archiveKey}
          defaultValue={archiveTeamText}
          onChange={(e) => setArchiveTeamText(e.target.value)}
          style={{ fontStyle: 'italic' }}
          onPressEnter={
            formIsInvalid(archiveTeamText, reason)
              ? undefined
              : () => onArchive(reason)
          }
        />
      </Modal>
    </>
  );
};
